import { flow } from 'fp-ts/function';
import { __, every, includes, isEqual, negate, stubFalse, stubTrue } from 'lodash/fp';

import {
    BookingEnginePaymentCardRequirement,
    ImmediateReservationPriceType,
    isPositiveAmount,
} from '@mews-distributor/common';
import { DecimalService } from '@mews/framework';
import { createHandler } from '@mews/std';
import { ImmediateReservationPrice } from '../../../../modules/entities/reservationPrices/types';

const getImmediateReservationPriceType = ({
    immediatelySettled,
    totalAmount,
}: ImmediateReservationPrice): ImmediateReservationPriceType => {
    const immediatelySettledDecimal = DecimalService.parse(immediatelySettled.grossValue);
    const orderTotalDecimal = DecimalService.parse(totalAmount.grossValue);
    if (DecimalService.isGreaterOrEqual(immediatelySettledDecimal, orderTotalDecimal)) {
        return ImmediateReservationPriceType.Full;
    }

    if (isPositiveAmount(immediatelySettled)) {
        return ImmediateReservationPriceType.Partial;
    }

    return ImmediateReservationPriceType.None;
};

export const isImmediatePriceFull = flow(getImmediateReservationPriceType, isEqual(ImmediateReservationPriceType.Full));

const isImmediatePricePartialOrFull = flow(
    getImmediateReservationPriceType,
    includes<ImmediateReservationPriceType>(__, [
        ImmediateReservationPriceType.Partial,
        ImmediateReservationPriceType.Full,
    ])
);

type ReservationPricesRequirePaymentCard = (handler: ImmediateReservationPrice[]) => boolean;
export const reservationPricesRequirePaymentCard = createHandler<
    BookingEnginePaymentCardRequirement,
    ReservationPricesRequirePaymentCard
>(
    {
        [BookingEnginePaymentCardRequirement.NotRequired]: stubFalse,
        [BookingEnginePaymentCardRequirement.AlwaysRequired]: stubTrue,
        [BookingEnginePaymentCardRequirement.NotRequiredForFullyPaidBookings]: negate(every(isImmediatePriceFull)),
        [BookingEnginePaymentCardRequirement.NotRequiredForFullyOrPartiallyPaidBookings]: negate(
            every(isImmediatePricePartialOrFull)
        ),
    },
    stubFalse
);
